import styled from "@emotion/styled";
import { useState } from "react";
import theme from "../../theme";
import userStorage from "../../utils/userStorage";
import SubHeader from "./subHeader";
import { Pagination } from "@mui/material";
import {
  DetailUserIcon,
  TrashCanIcon,
  WarningIcon,
} from "../../assets/icons/icons";
import AlertModal from "../../components/alertModal";
import CustomerDetailModal from "../../components/customerDetailModal/customerDetailModal";
import { Customer } from "../../types/types";
import useGetCustomer from "../../hooks/useGetCustomer";
import CustomerModifyModal from "../../components/customerModifyModal/customerModifyModal";
import CustomerRegisterModal from "../../components/customerRegisterModal/customerRegisterModal";
import useDeleteCustomer from "../../hooks/useDeleteCustomer";
import Spinner from "../../components/spinner";
import { SortArrow } from "../../components/sortArrow";
import { DotLoader } from "../../components/dotLoader";
import { handleClickForSort, textSubstring } from "../../utils/util";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { PageLayout } from "../../components/PageLayout";
import { useSystemSetting } from "../../hooks/useSystemSetting";
import { useWindowSize } from "@reactuses/core";
import { MobileManageCustomer } from "./mobileManageCustomer";
import { PhoneMaskToggle } from "../../components/PhoneMaskToggle";
import { useI18n } from "../../locales";

const { md } = theme.screen;

export interface FetchParams {
  searchKey: string;
  groupId: string;
}

const ManageCustomer: React.FC<{}> = () => {
  const user = userStorage.get();
  const systemSetting = useSystemSetting();
  const { width } = useWindowSize();
  const { t } = useI18n();
  const [searchKey, setSearchKey] = useState("");
  const [page, setPage] = useState(0);
  const [openDelModal, setOpenDelModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [openModify, setOpenModify] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [detailData, setDetailData] = useState<Customer>();
  const [idx, setIdx] = useState(0);
  const [sort, setSort] = useState<string | undefined>("");
  const [groupSubCode, setGroupSubCode] = useState(""); // 하위그룹코드(api용)

  const { data, isLoading } = useGetCustomer({
    page,
    searchKey,
    area: user?.area as string,
    sort,
    limit: 10,
    ...(groupSubCode &&
      groupSubCode !== "all" && { groupId: groupSubCode }),
  });

  const { mutate: deleteCustomer } = useDeleteCustomer({ seq: idx }, () =>
    setOpenDelModal(false)
  );

  const onClickBtn = ({ searchKey, groupId }: FetchParams) => {
    // 조회 버튼 클릭
    setPage(0);
    setSort("");
    setSearchKey(searchKey);
    setGroupSubCode(groupId);
  };

  const handlePageChange = (
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value - 1);
  };

  const openDeleteModal = (el: Customer) => {
    setIdx(el.seq);
    openCustomerDeleteModal();
  };

  const openCustomerDeleteModal = () => {
    setOpenDelModal(true);
  };

  const onPressYes = () => {
    deleteCustomer();
  };

  const onPressNo = () => {
    setOpenDelModal(false);
  };

  const userModalonPressNo = () => {
    setOpen(false);
  };

  const onClickDetailIcon = (el: Customer) => {
    setIdx(el.seq);
    openCustomerModifyModal();
  };

  const openCustomerModifyModal = () => {
    // 디테일 모달이 idx에 접근하기 위해 함수를 밖으로 뺌
    setOpen(true);
  };

  const onClickModify = () => {
    setOpen(false);
    setOpenModify(true);
  };

  const onPressNoModify = () => {
    setOpenModify(false);
  };

  const makeTableHeadList = (id: string, title: string, width: number) => {
    return (
      <TableHeaderList
        onClick={() => {
          handleClickForSort(id, setSort);
        }}
        width={width}
        disabled={data?.list.length === 0}
      >
        {title} <SortArrow id={id} state={sort} />
      </TableHeaderList>
    );
  };

  return (
    <PageLayout
      subHeader={
        width > md && (
          <SubHeader
            onClickBtn={onClickBtn}
            openModal={() => setOpenRegister(true)}
            systemSetting={systemSetting("user")}
          />
        )
      }
      withBreadcrumb
    >
      {width <= md && (
        <MobileManageCustomer
          data={data}
          isLoading={isLoading}
          systemSetting={systemSetting("user")}
          onClickBtn={onClickBtn}
          sort={sort}
          setSort={setSort}
          setOpenRegisterModal={setOpenRegister}
          onClickDetailIcon={onClickDetailIcon}
          onClickDeleteIcon={openDeleteModal}
        />
      )}
      {width > md && (
        <>
          <BodyHeader>
            <span style={{ marginRight: "5px" }}>{t.manageCustomer.ownerInfo} - </span>
            {isLoading && <DotLoader />}
            {!isLoading && data && <span>{data.total_items + " " + t.manageCustomer.peopleUnit}</span>}
            {!isLoading && !data && <span>{`0 ${t.manageCustomer.peopleUnit}`}</span>}
          </BodyHeader>
          <TableHeader>
            {makeTableHeadList(`seq`, t.manageCustomer.ownerNumber, 100)}
            {makeTableHeadList(`id`, t.manageCustomer.contact, 120)}
            {makeTableHeadList(`name`, t.manageCustomer.name, 150)}
            {makeTableHeadList(`address`, t.manageCustomer.address, 288)}
            {makeTableHeadList(`address_detail`, t.manageCustomer.detailAddress, 170)}
            {/* {makeTableHeadList(`birth`, `생년월일`, 100)} */}
            {/* {makeTableHeadList(`group_name`, `담당기관`, 110)} */}
            {makeTableHeadList(`group_name`, t.manageCustomer.group, 110)}
          </TableHeader>
          <TableBody>
            {isLoading && <Spinner />}
            {data &&
              data.list.length > 0 &&
              data.list.map((el) => (
                <TableList key={el.seq.toString()}>
                  {/* 소유자 번호 */}
                  <MobInfo style={{ minWidth: "100px", maxWidth: "100" }}>
                    {el.seq}
                  </MobInfo>

                  {/* 소유자 아이디 */}
                  <MobInfo style={{ minWidth: "120px" }}>
                    {/* {el.id} */}
                    <PhoneMaskToggle
                      text={el.id}
                      who={1} // 소유자
                      userIdx={user?.idx}
                      name={el.name}
                    />
                  </MobInfo>

                  {/* 소유자 이름 */}
                  <CustomTooltip
                    title={el?.name}
                    active={el?.name?.length > 10}
                  >
                    <MobInfo
                      style={{
                        minWidth: "150px",
                      }}
                    >
                      {textSubstring(el.name, 0, 10)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 소유자 주소 */}
                  <CustomTooltip
                    title={el?.address}
                    active={el?.address?.length > 25}
                  >
                    <MobInfo style={{ minWidth: "288px" }}>
                      {textSubstring(el.address, 0, 25)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 시용자 상세주소 */}
                  <CustomTooltip
                    title={el?.address_detail}
                    active={el?.address_detail?.length > 11}
                  >
                    <MobInfo style={{ minWidth: "170px" }}>
                      {textSubstring(el.address_detail, 0, 11)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 소유자 생년월일 */}
                  {/*<MobInfo style={{ minWidth: "100px" }}>
                    {el.birth}
                  </MobInfo> */}

                  {/* 담당기관 (그룹) */}
                  <CustomTooltip
                    title={el?.group_name}
                    active={el?.group_name?.length > 6}
                  >
                    <MobInfo style={{ minWidth: "110px" }}>
                      {textSubstring(el.group_name, 0, 6)}
                    </MobInfo>
                  </CustomTooltip>

                  <IconBox>
                    {/* 정보 상세보기 아이콘 */}
                    <CustomTooltip title={t.manageCustomer.viewDetailInfo} active>
                      <IconWrapper onClick={() => onClickDetailIcon(el)}>
                        <DetailUserIcon />
                      </IconWrapper>
                    </CustomTooltip>

                    <CustomTooltip title={t.manageCustomer.deleteAccount} active>
                      <IconWrapper
                        onClick={() => {
                          if (!systemSetting("user")?.delete) return;

                          openDeleteModal(el);
                        }}
                        disabled={!systemSetting("user")?.delete}
                      >
                        <TrashCanIcon />
                      </IconWrapper>
                    </CustomTooltip>
                  </IconBox>
                </TableList>
              ))}
            {!isLoading && data && data.list.length === 0 && (
              <NoDataMassage>{t.manageCustomer.noDataFound}</NoDataMassage>
            )}
          </TableBody>
        </>
      )}

      {!isLoading && (
        <PaginationFooter>
          <Pagination
            count={data?.total_page}
            page={page + 1}
            onChange={handlePageChange}
            siblingCount={width <= md ? 1 : 3}
            size={width <= md ? "small" : "medium"}
          />
        </PaginationFooter>
      )}

      {/* 소유자 등록 모달 */}
      {openRegister && (
        <CustomerRegisterModal
          open={openRegister}
          onPressNo={() => setOpenRegister(false)}
          systemSetting={systemSetting("user")}
        />
      )}
      {/* 소유자 상세보기 모달 */}
      {open && (
        <CustomerDetailModal
          seq={idx}
          open={open}
          onPressNo={userModalonPressNo}
          setDetailData={setDetailData}
          onClickModify={onClickModify}
          detailData={detailData}
          systemSetting={systemSetting("user")}
        />
      )}
      {/* 소유자 수정하기 모달 */}
      {openModify && (
        <CustomerModifyModal
          open={openModify}
          data={detailData}
          seq={idx}
          onPressNo={onPressNoModify}
          setDetailData={setDetailData}
          systemSetting={systemSetting("user")}
        />
      )}
      <AlertModal
        open={openDelModal}
        onPressNo={onPressNo}
        title={t.manageCustomer.deleteConfirmTitle}
        content={t.manageCustomer.deleteConfirmContent}
        confirmText={t.common.delete}
        onPressYes={onPressYes}
        icon={<WarningIcon />}
      />
    </PageLayout>
  );
};

const BodyHeader = styled.h1`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-bottom: 15px;
  border-bottom: 3px solid ${theme.color.neutral_200};
  display: flex;
  align-items: center;
`;

const TableHeader = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 30px;
  padding-left: 20px;
  padding-right: 20px;
`;

const TableHeaderList = styled.button<{ width: number }>`
  min-width: ${({ width }) => `${width}px`};
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  white-space: pre-line; /* \n 개행 허용 */
  text-align: center;
  color: ${theme.color.neutral_500};
  display: flex;
  justify-content: center;
  cursor: pointer;
  transition: 200ms;
  &:hover {
    font-weight: bold;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  flex: 1;
`;

const TableList = styled.div`
  background: ${theme.color.white};
  border-radius: 10px;
  width: 100%;
  height: 46px;
  display: flex;
  gap: 30px;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
`;

const MobInfo = styled.span`
  display: block;
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.neutral_600};
  text-align: center;
  white-space: normal;
`;

const IconBox = styled.div`
  display: flex;
  gap: 15px;
  flex: 1;
  justify-content: flex-end;
  padding-right: 33px;
`;

const IconWrapper = styled.button<{ disabled?: boolean }>`
  width: 23px;
  height: 23px;
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "defalut" : "pointer")};
  transition: 200ms;
  &:hover {
    scale: ${({ disabled }) => (disabled ? "100%" : "120%")};
  }
`;

const PaginationFooter = styled.footer`
  display: flex;
  justify-content: center;
  margin-top: 30px;

  @media (max-width: ${md}px) {
    height: 56px;
    margin-top: 0px;
    align-items: center;

    nav {
      padding: 14px 0px;
    }

    button {
      min-width: 28px;
      height: 28px;
      font-size: ${theme.fontSize.FONT_1};
      font-weight: ${theme.fontWeight.SEMI_BOLD};
      color: ${theme.color.neutral_500};
    }
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  padding: 100px;
  color: ${theme.color.neutral_500};
`;

export default ManageCustomer;
